import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { coachServiceFixed } from '@/services/coachServiceFixed';
import { useTeams } from '@/hooks/useTeams';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

interface CoachingGoalsModalProps {
  open: boolean;
  onClose: () => void;
  onGoalAdded: () => void;
}

const CoachingGoalsModal: React.FC<CoachingGoalsModalProps> = ({ 
  open, 
  onClose, 
  onGoalAdded 
}) => {
  const { profile } = useAuth();
  const { user: internalUser } = useInternalAuth();
  const { teams } = useTeams();
  const [loading, setLoading] = useState(false);

  const currentUserId = profile?.id || internalUser?.id;
  const userTeams = teams.filter(team => team.created_by === currentUserId);

  const [formData, setFormData] = useState({
    team_id: '',
    goal_title: '',
    goal_description: '',
    goal_type: 'attendance' as 'attendance' | 'performance' | 'satisfaction' | 'skill_development' | 'wins',
    target_value: '',
    target_date: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUserId) return;

    try {
      setLoading(true);
      
      const actualCoachId = await coachServiceFixed.resolveCoachId(currentUserId);
      console.log('🎯 Using resolved coach ID for new goal:', actualCoachId);
      
      const goalData = {
        coach_id: actualCoachId,
        team_id: formData.team_id ? parseInt(formData.team_id) : undefined,
        goal_title: formData.goal_title,
        goal_description: formData.goal_description || undefined,
        goal_type: formData.goal_type,
        target_value: parseFloat(formData.target_value),
        current_value: 0,
        target_date: formData.target_date || undefined,
        status: 'active' as const
      };

      await coachServiceFixed.createCoachingGoal(goalData);
      toast.success('Coaching goal created successfully!');
      onGoalAdded();
      onClose();
      
      // Reset form
      setFormData({
        team_id: '',
        goal_title: '',
        goal_description: '',
        goal_type: 'attendance',
        target_value: '',
        target_date: ''
      });
    } catch (error) {
      console.error('Error creating coaching goal:', error);
      toast.error('Failed to create coaching goal');
    } finally {
      setLoading(false);
    }
  };

  const getGoalTypeDescription = (type: string) => {
    switch (type) {
      case 'attendance':
        return 'Percentage (0-100)';
      case 'performance':
        return 'Percentage (0-100)';
      case 'satisfaction':
        return 'Rating (1-5)';
      case 'skill_development':
        return 'Percentage (0-100)';
      case 'wins':
        return 'Number of wins';
      default:
        return 'Value';
    }
  };

  const getGoalTypeLabel = (type: string) => {
    switch (type) {
      case 'attendance':
        return 'Team Attendance';
      case 'improvement':
        return 'Player Improvement Rate';
      case 'satisfaction':
        return 'Team Satisfaction';
      case 'growth':
        return 'Team Growth';
      default:
        return type;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#0d1117] border border-gray-800 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="text-sports-green">Create Coaching Goal</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="goal_title">Goal Title</Label>
            <Input
              id="goal_title"
              value={formData.goal_title}
              onChange={(e) => setFormData(prev => ({ ...prev, goal_title: e.target.value }))}
              className="bg-gray-900 border-gray-700 text-white"
              placeholder="e.g., Improve Team Attendance"
              required
            />
          </div>

          <div>
            <Label htmlFor="goal_type">Goal Type</Label>
            <Select 
              value={formData.goal_type} 
              onValueChange={(value) => setFormData(prev => ({ ...prev, goal_type: value as 'attendance' | 'performance' | 'satisfaction' | 'skill_development' | 'wins' }))}
            >
              <SelectTrigger className="bg-gray-900 border-gray-700 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-gray-900 border-gray-700">
                <SelectItem value="attendance">Team Attendance</SelectItem>
                <SelectItem value="performance">Player Performance</SelectItem>
                <SelectItem value="satisfaction">Team Satisfaction</SelectItem>
                <SelectItem value="skill_development">Skill Development</SelectItem>
                <SelectItem value="wins">Team Wins</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="target_value">
              Target Value ({getGoalTypeDescription(formData.goal_type)})
            </Label>
            <Input
              id="target_value"
              type="number"
              min="0"
              max={formData.goal_type === 'satisfaction' ? 5 : 100}
              step={formData.goal_type === 'satisfaction' ? '0.1' : '1'}
              value={formData.target_value}
              onChange={(e) => setFormData(prev => ({ ...prev, target_value: e.target.value }))}
              className="bg-gray-900 border-gray-700 text-white"
              required
            />
          </div>

          <div>
            <Label htmlFor="team_id">Team (Optional)</Label>
            <Select 
              value={formData.team_id} 
              onValueChange={(value) => setFormData(prev => ({ ...prev, team_id: value }))}
            >
              <SelectTrigger className="bg-gray-900 border-gray-700 text-white">
                <SelectValue placeholder="Select a team (optional)" />
              </SelectTrigger>
              <SelectContent className="bg-gray-900 border-gray-700">
                {userTeams.map((team) => (
                  <SelectItem key={team.team_id} value={team.team_id.toString()}>
                    {team.team_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="target_date">Target Date (Optional)</Label>
            <Input
              id="target_date"
              type="date"
              value={formData.target_date}
              onChange={(e) => setFormData(prev => ({ ...prev, target_date: e.target.value }))}
              className="bg-gray-900 border-gray-700 text-white"
            />
          </div>

          <div>
            <Label htmlFor="goal_description">Description (Optional)</Label>
            <Textarea
              id="goal_description"
              value={formData.goal_description}
              onChange={(e) => setFormData(prev => ({ ...prev, goal_description: e.target.value }))}
              className="bg-gray-900 border-gray-700 text-white"
              rows={3}
              placeholder="Describe your goal and how you plan to achieve it..."
            />
          </div>

          <div className="flex gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-sports-green text-black hover:bg-green-600"
            >
              {loading ? 'Creating...' : 'Create Goal'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CoachingGoalsModal;